import React, { useState, useEffect, useCallback } from 'react';
import { Log, useTranslation } from '@fanfanlo';
// 修改：导入语句已从 './TextRecognitionControl.css' 更改为 CSS 模块
import styles from './TextRecognitionControl.module.css';
import { autoWebViewJs } from '@droid/android';

const fileLog = new Log(false, 'TextRecognitionControl.tsx');
interface TextMatchConfig {
  regexPatterns?: string[];
  eventName: string;
  jsFn?: string;
}

interface TextMatchResult {
  matchedText?: string;
  startIndex?: number;
  endIndex?: number;
  textBlocks?: Array<any>;
}

/**
 * 文字识图控制组件
 *
 * 功能：
 * 1. 启动/停止屏幕识图
 * 2. 配置识图匹配规则（正则或JS函数）
 * 3. 监听识图事件和匹配事件
 * 4. 显示识图状态和匹配结果
 */
export const TextRecognitionControl: React.FC = () => {
  const uiLog = fileLog.sub(false, 'TextRecognitionControl_ui');
  const { t } = useTranslation('homepage/components/screen-recognition/content');

  // 公共事件名称
  const EVENT_RECOGNITION_STARTED = 'screen_recognition_started';
  const EVENT_RECOGNITION_START_FAILED = 'screen_recognition_start_failed';
  const EVENT_RECOGNITION_STOPPED = 'screen_recognition_stopped';
  const EVENT_RECOGNITION_STOP_FAILED = 'screen_recognition_stop_failed';

  // 私有事件名称
  const PRIVATE_EVENT_START_SUCCESS = 'text_recognition_start_success';
  const PRIVATE_EVENT_START_FAILED = 'text_recognition_start_failed';
  const PRIVATE_EVENT_STOP_SUCCESS = 'text_recognition_stop_success';
  const PRIVATE_EVENT_STOP_FAILED = 'text_recognition_stop_failed';

  // 状态
  const [isRunning, setIsRunning] = useState(false);
  const [matchResults, setMatchResults] = useState<TextMatchResult[]>([]);
  const [useRegex, setUseRegex] = useState(true);
  const [regexPatterns, setRegexPatterns] = useState('');
  const [jsFunction, setJsFunction] = useState('');
  const [eventName, setEventName] = useState('text_matched');
  const [lastMatchedText, setLastMatchedText] = useState('');
  const [statusMessage, setStatusMessage] = useState('');

  // 处理启动识图
  const handleStart = useCallback(() => {
    const effLog = uiLog.sub(false, 'handleStart_fn');
    effLog.info('TextRecognitionControl: 启动屏幕识图');

    try {
      // 构建匹配配置
      const config: TextMatchConfig = {
        eventName: eventName,
      };

      if (useRegex && regexPatterns) {
        config.regexPatterns = regexPatterns.split('\n').filter(p => p.trim());
      } else if (!useRegex && jsFunction) {
        config.jsFn = jsFunction;
      } else {
        setStatusMessage(t('TextRecognitionControl.errorNoConfig'));
        return;
      }

      const configJson = JSON.stringify(config);
      effLog.info('TextRecognitionControl: 配置JSON', configJson);

      // 调用 Android 接口启动识图
      const script = `
      com.fanfanlo.lib.screenrecorder.ScreenRecognitionController.start(
        '${configJson}',
        '${EVENT_RECOGNITION_STARTED}',
        '${EVENT_RECOGNITION_START_FAILED}'
      );
      `;
      effLog.info('TextRecognitionControl: 调用启动脚本', script);
      autoWebViewJs.callScript<boolean>(script)
    } catch (error) {
      effLog.error('TextRecognitionControl: 启动异常', error);
      setStatusMessage(t('TextRecognitionControl.startException'));
    }
  }, [useRegex, regexPatterns, jsFunction, eventName, t]);

  // 处理停止识图
  const handleStop = useCallback(() => {
    const effLog = uiLog.sub(false, 'handleStop_fn');
    effLog.info('TextRecognitionControl: 停止屏幕识图');

    try {
      const script = `
      com.fanfanlo.lib.screenrecorder.ScreenRecognitionController.stop('${PRIVATE_EVENT_STOP_SUCCESS}', '${PRIVATE_EVENT_STOP_FAILED}');`;
      autoWebViewJs.callScript<boolean>(script);
    } catch (error) {
      effLog.error('TextRecognitionControl: 停止异常', error);
      setStatusMessage(t('TextRecognitionControl.stopException'));
    }
  }, [t]);

  // 检查识图状态
  const checkRunningStatus = useCallback(() => {
    const effLog = uiLog.sub(false, 'checkRunningStatus_fn');
    try {
      const script = `
      com.fanfanlo.lib.screenrecorder.ScreenRecognitionController.isRunning();`;
      const {javaResultString, errMsg} = autoWebViewJs.callScript<string>(script);
      if(errMsg){
        effLog.warn('TextRecognitionControl: 检查状态脚本调用异常', errMsg);
        return;
      }
      const running = javaResultString === 'true';
      setIsRunning(running);
      effLog.info('TextRecognitionControl: 识图状态 -', running);
    } catch (error) {
      effLog.warn('TextRecognitionControl: 检查状态异常', error);
    }
  }, []);

  // 事件监听
  useEffect(() => {
    const effLog = uiLog.sub(false, 'useEffect_eventListeners');
    effLog.info('TextRecognitionControl: 设置事件监听');

    // 私有事件回调
    const onPrivateStartSuccess = () => {
      effLog.info('TextRecognitionControl: 私有事件 - 启动成功');
      effLog.info('TextRecognitionControl: 识图已启动');
      setIsRunning(true);
      setStatusMessage(t('TextRecognitionControl.recognitionStarted'));
      setMatchResults([]);
    };

    const onPrivateStartFailed = () => {
      effLog.warn('TextRecognitionControl: 私有事件 - 启动失败');
      const customEvent = event as CustomEvent;
      const error = customEvent.detail?.error || '未知错误';
      effLog.error('TextRecognitionControl: 启动失败 -', error);
      setStatusMessage(`${t('TextRecognitionControl.startFailed')}: ${error}`);
    };

    const onPrivateStopSuccess = () => {
      effLog.info('TextRecognitionControl: 私有事件 - 停止成功');
      effLog.info('TextRecognitionControl: 识图已停止');
      setIsRunning(false);
      setStatusMessage(t('TextRecognitionControl.recognitionStopped'));
    };

    const onPrivateStopFailed = () => {
      effLog.warn('TextRecognitionControl: 私有事件 - 停止失败');
      const customEvent = event as CustomEvent;
      const error = customEvent.detail?.error || '未知错误';
      effLog.error('TextRecognitionControl: 停止失败 -', error);
      setStatusMessage(`${t('TextRecognitionControl.stopFailed')}: ${error}`);
    };

    // 文字匹配事件回调
    const onTextMatched = (event: Event) => {
      const customEvent = event as CustomEvent;
      const matchData = customEvent.detail;
      effLog.info('TextRecognitionControl: 文字匹配成功 -', matchData);
      setLastMatchedText(matchData?.matchedText || '');
      if (matchData) {
        setMatchResults(prev => [...prev.slice(-9), matchData]);
      }
      setStatusMessage(`${t('TextRecognitionControl.matched')}: ${matchData?.matchedText}`);
    };

    // 添加事件监听
    window.addEventListener(PRIVATE_EVENT_START_SUCCESS, onPrivateStartSuccess);
    window.addEventListener(PRIVATE_EVENT_START_FAILED, onPrivateStartFailed);
    window.addEventListener(PRIVATE_EVENT_STOP_SUCCESS, onPrivateStopSuccess);
    window.addEventListener(PRIVATE_EVENT_STOP_FAILED, onPrivateStopFailed);
    window.addEventListener(eventName, onTextMatched);

    // 检查初始状态
    checkRunningStatus();

    // 清理监听
    return () => {
      window.removeEventListener(PRIVATE_EVENT_START_SUCCESS, onPrivateStartSuccess);
      window.removeEventListener(PRIVATE_EVENT_START_FAILED, onPrivateStartFailed);
      window.removeEventListener(PRIVATE_EVENT_STOP_SUCCESS, onPrivateStopSuccess);
      window.removeEventListener(PRIVATE_EVENT_STOP_FAILED, onPrivateStopFailed);
      window.removeEventListener(eventName, onTextMatched);
      effLog.info('TextRecognitionControl: 清理事件监听');
    };
  }, [eventName, checkRunningStatus, t]);

  return (
    <div className={styles.textRecognitionControl}>
      <div className={styles.controlSection}>
        <h2>{t('TextRecognitionControl.title')}</h2>

        {/* 状态显示 */}
        <div className={styles.statusDisplay}>
          <div className={`${styles.statusIndicator} ${isRunning ? styles.running : styles.stopped}`}>
            {isRunning ? '🟢' : '🔴'} {isRunning ? t('TextRecognitionControl.running') : t('TextRecognitionControl.stopped')}
          </div>
          {statusMessage && <div className={styles.statusMessage}>{statusMessage}</div>}
        </div>

        {/* 匹配配置 */}
        <div className={styles.configSection}>
          <h3>{t('TextRecognitionControl.matchConfig')}</h3>

          {/* 配置类型选择 */}
          <div className={styles.configType}>
            <label>
              <input
                type="radio"
                name="configType"
                checked={useRegex}
                onChange={() => setUseRegex(true)}
              />
              {t('TextRecognitionControl.useRegex')}
            </label>
            <label>
              <input
                type="radio"
                name="configType"
                checked={!useRegex}
                onChange={() => setUseRegex(false)}
              />
              {t('TextRecognitionControl.useJsFunction')}
            </label>
          </div>

          {/* 正则表达式配置 */}
          {useRegex && (
            <div className={styles.regexConfig}>
              <label>{t('TextRecognitionControl.regexPatterns')}</label>
              <textarea
                value={regexPatterns}
                className={styles.inputField} // 添加 className
                onChange={(e) => setRegexPatterns(e.target.value)}
                placeholder={t('TextRecognitionControl.regexPlaceholder')}
                rows={4}
              />
            </div>
          )}

          {/* JS 函数配置 */}
          {!useRegex && (
            <div className={styles.jsConfig}>
              <label>{t('TextRecognitionControl.jsFunction')}</label>
              <textarea
                className={styles.inputField} // 添加 className
                value={jsFunction}
                onChange={(e) => setJsFunction(e.target.value)}
                placeholder={t('TextRecognitionControl.jsFunctionPlaceholder')}
                rows={4}
              />
            </div>
          )}

          {/* 事件名称 */}
          <div className={styles.eventNameConfig}>
            <label>{t('TextRecognitionControl.eventName')}</label>
            <input
              className={styles.inputField} // 添加 className
              type="text"
              value={eventName}
              onChange={(e) => setEventName(e.target.value)}
              placeholder={t('TextRecognitionControl.eventNamePlaceholder')}
            />
          </div>
        </div>

        {/* 控制按钮 */}
        <div className={styles.buttonGroup}>
          <button
            onClick={handleStart}
            disabled={isRunning}
            className={styles.btnStart}
          >
            {t('TextRecognitionControl.startButton')}
          </button>
          <button
            onClick={handleStop}
            disabled={!isRunning}
            className={styles.btnStop}
          >
            {t('TextRecognitionControl.stopButton')}
          </button>
        </div>

        {/* 匹配结果 */}
        {lastMatchedText && (
          <div className={styles.matchResult}>
            <h3>{t('TextRecognitionControl.lastMatched')}</h3>
            <div className={styles.matchedText}>{lastMatchedText}</div>
          </div>
        )}

        {/* 历史结果 */}
        {matchResults.length > 0 && (
          <div className={styles.historyResults}>
            <h3>{t('TextRecognitionControl.matchHistory')}</h3>
            <div className={styles.resultsList}>
              {matchResults.map((result, index) => (
                <div key={index} className={styles.resultItem}>
                  <span className={styles.resultText}>{result.matchedText}</span>
                  <span className={styles.resultPosition}>
                    [{result.startIndex}, {result.endIndex}]
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};